import { Component, HostListener, Injector, OnDestroy } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Data, NavigationEnd, NavigationError, ResolveEnd, ResolveStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RouteConf } from './core/models/route-config';
import { selectLayoutTitle, LayoutActionType, State as StateLayout } from './store/layout/layout.reducers';
import { DynaConfActionType, State as StateShared } from './store/shared/shared.reducers';
import { BaseAction } from './store/store.actions';
import { DialogService } from './shared/services/dialog.service';
import { MatIconRegistry } from '@angular/material/icon';
import { back_to_tab, foward_2, pip } from './shared/models/entities';
import { ScrollComponentForComponent } from './shared/components/scroll-component-for/scroll-component-for.component';
import { dbg } from './shared/utils/debug-ring';
import { AuthSyncService } from './auth/services/auth-sync.service';
import { isAppPath, routePathOf } from './auth/utils/return-url';
import { ChunkErrorRecoveryService, isChunkLoadError } from './core/services/chunk-error-recovery.service';
import { ReleaseNotesStateService } from './core/services/release-notes-state.service';
import { DateFormatService } from './core/services/date-format.service';
import { CellContextMenuService } from './core/services/cell-context-menu.service';
import { NumberFormatService } from './core/services/number-format.service';
import { UserLanguageService } from './core/services/user-language.service';
import { MenuLayoutService } from './core/services/menu-layout.service';
import { AvatarService } from './core/services/avatar.service';
import { ToastDurationService } from './core/services/toast-duration.service';
import { ListScrollService } from './core/services/list-scroll.service';
import { ThemeService } from './core/services/theme.service';
import { ContrastService } from './core/services/contrast.service';
import { CvdService } from './core/services/cvd.service';
import { FontScaleService } from './core/services/font-scale.service';
import { OverlayTeardownService } from './core/services/overlay-teardown.service';
import {
  PushNotificationService,
  PUSH_POSTED_ENDPOINT_STORAGE_KEY,
} from './core/push/push-notification.service';
import { PushDeepLinkService } from './core/push/push-deep-link.service';
import { OfflineSessionService } from './core/offline/offline-session.service';
import { PUSH_DEVICE_ID_STORAGE_KEY, PUSH_DEVICE_SECRET_STORAGE_KEY } from './core/push/push-device-id';
import { LOGOUT_PENDING_STORAGE_KEY } from './core/offline/offline-identity';
import { APP_LANG_STORAGE_KEY } from './shared/services/app-locale.service';
import { clearStorage, readStorage, writeStorage } from './shared/utils/safe-storage';
import { QUOTA_DIAG_STORAGE_KEY } from './shared/utils/storage-quota-diag';



/** How long the identity-switch notice stays up. Longer than the usual 5s toast: it explains an
 *  interruption the user did not ask for (their screen was replaced) and may cost them unsaved
 *  input, so it must survive the moment of confusion it is there to answer. Lives here, next to the
 *  only consumer — the raising of the toast is AppComponent's job, not the auth service's. */
export const IDENTITY_SWITCH_NOTICE_MS = 10000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends ScrollComponentForComponent implements OnDestroy {

  public nameProd = environment.name || undefined;

  /**
   * The id the skip link targets. Every routed shell declares `<main id="main-content">` — swept by
   * `src/app/a11y/skip-link.spec.ts` — and nothing outside a shell does.
   */
  static readonly SKIP_TARGET_ID = 'main-content';

  /**
   * Whether the skip link renders. Only `/app` routes mount a shell, so only they have a target:
   * on `/auth` (login, enroll) the link would be the first Tab stop and lead nowhere. Decided off
   * the ROUTE on NavigationEnd rather than off the DOM, because a template binding asking
   * `document.getElementById` changes value between the root's check and the shell's render, which
   * dev mode reports as ExpressionChangedAfterItHasBeenChecked.
   */
  showSkipLink = false;

  /**
   * Moves focus to the shell's `<main>` in code. A bare `href="#main-content"` does NOT work in
   * this app: `index.html` sets `<base href="/">`, so the browser resolves the fragment against `/`
   * rather than the current page, and activating the link on `/app/shop/...` loads `/#main-content`
   * — a full document reload that drops the page and never moves focus. The `href` stays so the
   * element is a real link (role, focusability); the default navigation is always prevented.
   */
  skipToContent(event: Event): void {
    event.preventDefault();
    const target = document.getElementById(AppComponent.SKIP_TARGET_ID);
    // focus() scrolls the target into view on its own. A missing target does nothing rather than
    // navigating: the route gate should make it unreachable, and a no-op beats a reload.
    target?.focus();
  }



  // ir a set insert

  @HostListener('document:keydown.shift.control.z')
  doSomething() {
    if (true) {
      if (!this.router.url.includes('set-insert')) {

        this.router.navigateByUrl('app/set-insert-out').then();
      } else {

        this.dialogService.openSnack('common.message.alreadyOnRequestedUrl', 'info', 3000);
      }
    }
  }




  title = 'Gestionale15';

  /** Kept so the notice subscription dies with the component (see ngOnDestroy). */
  private identitySwitchNotice?: Subscription;

  constructor(public router: Router, private storeShared: Store<StateShared>, private storeLayout: Store<StateLayout>, private dialogService: DialogService,
    private titleService: Title, private translateService: TranslateService, iconRegistry: MatIconRegistry, sanitizer: DomSanitizer, private authSync: AuthSyncService, private chunkErrorRecovery: ChunkErrorRecoveryService,
    private releaseNotesState: ReleaseNotesStateService, private dateFormatService: DateFormatService, private numberFormatService: NumberFormatService,
    private cellContextMenu: CellContextMenuService, private userLanguage: UserLanguageService,
    private menuLayoutService: MenuLayoutService, private avatarService: AvatarService, private toastDurationService: ToastDurationService,
    private listScrollService: ListScrollService, private themeService: ThemeService,
    private contrastService: ContrastService,
    private cvdService: CvdService,
    private fontScaleService: FontScaleService,
    private overlayTeardown: OverlayTeardownService, private pushNotificationService: PushNotificationService,
    private pushDeepLinkService: PushDeepLinkService,
    private offlineSession: OfflineSessionService,
    private injector: Injector) {
    super();
    // icons
    iconRegistry.addSvgIconLiteral('back_to_tab', sanitizer.bypassSecurityTrustHtml(back_to_tab));
    iconRegistry.addSvgIconLiteral('pip', sanitizer.bypassSecurityTrustHtml(pip));
    iconRegistry.addSvgIconLiteral('foward_2', sanitizer.bypassSecurityTrustHtml(foward_2));

    storeLayout.select(selectLayoutTitle).pipe(
      debounceTime(environment.defaultDebounceTime),
      distinctUntilChanged(),
      switchMap((tit: string) => {
        dbg('APP.title.translate', { storeTitle: tit });
        return this.translateService.stream(tit);
      })
    ).subscribe((tit: string) => {
      dbg('APP.title.setTitle', { translated: tit, prevDocTitle: document.title });
      this.titleService.setTitle(tit);
    });

    router.events.pipe(
      filter(e => e instanceof ResolveStart)
    ).subscribe((e: any) => {
      // this.store.dispatch(new BaseAction(LayoutActionType.Loading, true));
      this.storeShared.dispatch(new BaseAction(DynaConfActionType.ConfChange, null));
    });

    router.events.pipe(
      filter(e => e instanceof ResolveEnd)
    ).subscribe((e: any) => {
      const routeConf: RouteConf | null = this.findData(e.state.root);
      if (routeConf) {
        const translationKey = routeConf.translationKey ?? routeConf.code;
        this.storeLayout.dispatch(new BaseAction(LayoutActionType.Title, `${translationKey}.title`));
      }

      // The app bar's back control, from the same place and on the same event as the title. It is
      // dispatched for EVERY resolved route, including the ones that declare none: a route that
      // said nothing would otherwise inherit the previous screen's arrow and point at a destination
      // unrelated to what is on screen. A screen with no RouteConf sets and clears its own -- AFTER
      // this, since ResolveEnd precedes activation, so the clear here never lands on top of it.
      //
      // It sat inside the `if (routeConf)` above and therefore did the opposite of what this comment
      // says: a route declaring no RouteConf cleared nothing and inherited the previous screen's
      // arrow. The two in-tree cases (the kiosk detail and wizard) only escaped it by clearing their
      // own in ngOnDestroy -- and the layout slice is persisted to the `${appVersion}_stat` blob and
      // rehydrated at boot, so a teardown where ngOnDestroy does not run left the arrow pointing at
      // the kiosk fleet from the next session's first screen onwards.
      this.storeLayout.dispatch(new BaseAction(LayoutActionType.BackLink, routeConf?.backLink ?? null));

      this.storeShared.dispatch(new BaseAction(DynaConfActionType.ConfChange, routeConf));
    });

    router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe((e) => {
      this.showSkipLink = isAppPath(routePathOf(e.urlAfterRedirects));
    });

    router.events.pipe(
      filter(e => e instanceof NavigationError)
    ).subscribe((e: any) => {
      if (isChunkLoadError(e.error)) {
        this.chunkErrorRecovery.tryReloadOrNotify();
      }
    });

    // An identity switch (another user signed in elsewhere, and the shared refresh cookie made this tab
    // theirs — learnt either off the auth channel or first-hand from this tab's own silent refresh)
    // closes this tab's dialogs and navigates it off the stale page. All of it is silent from the
    // user's side, since the trigger happened elsewhere. Say why, or the screen swap reads as a crash.
    // Owned here rather than in AuthSyncService so that service stays free of a hard Material
    // dependency (see identitySwitchNavigated$); AppComponent is always mounted, so nothing is missed,
    // and the toast still goes through DialogService → AppSnackBarService, which applies the user's
    // toast-duration preference.
    this.identitySwitchNotice = this.authSync.identitySwitchNavigated$.subscribe(() => {
      const ref = this.dialogService.openSnack('auth.session.switchedUser', 'warning', IDENTITY_SWITCH_NOTICE_MS);
      // Exempt it from overlay teardown, or the very next one wipes the explanation: the switch has
      // just navigated this tab home, and if that landing fetch 401s and its refresh fails,
      // AuthService.clearSession() runs closeAll() milliseconds later — leaving the user on the login
      // page with their screen replaced and nothing saying why, the exact failure this notice prevents.
      //
      // ACCEPTED EDGE — the exemption protects the notice from a TEARDOWN, not from another toast.
      // Only one snackbar is on screen at a time, so a toast opened right after this one replaces it:
      // on the refresh path the switch aborts the in-flight request with the ORIGINAL 401
      // (IdentitySwitchedError → handle401's carve-out), and a caller with its own 401 handling may
      // toast that. Left alone deliberately: suppressing or re-raising the notice would mean this
      // component policing every other feature's error reporting, and the user still gets a message —
      // just a less useful one. The rescue itself (dialogs closed, page left, identity applied) does
      // not depend on the toast.
      this.overlayTeardown.exemptSnackBarFromTeardown(ref);
    });

    this.releaseNotesState.initLoginPrompt();
    this.dateFormatService.init();
    this.numberFormatService.init();
    this.cellContextMenu.init();
    this.userLanguage.init();
    this.menuLayoutService.init();
    this.avatarService.init();
    this.toastDurationService.init();
    this.listScrollService.init();
    this.themeService.init();
    this.contrastService.init();
    this.cvdService.init();
    this.fontScaleService.init();
    // Reads the current state and wires the per-session re-POST (worker registration is lazy).
    // Never prompts — see PushNotificationService.enable().
    this.pushNotificationService.init();
    // Routes to the record a tapped notification pointed at. See PushDeepLinkService for why the
    // handler must run inside zone.run.
    this.pushDeepLinkService.listen();
    // Offline: run offline mode if the boot entered it (reconnect, cross-tab agreement, expiry), and
    // register the service worker for every session so the app shell is cached before the network
    // goes away (CLAUDE.md, "Offline mode" and "Service worker").
    this.offlineSession.init();
    this.startOfflineShell();
  }

  /**
   * The service worker and the switch to a new build (OfflineShellService), loaded on demand: it
   * registers the worker only once the page has loaded and watches for a build the worker installs
   * later, so nothing of it is needed for the first paint, and keeping its code out of `main` keeps
   * the initial bundle under its budget. The chunk is the one Intelligence > Scan's offline screen
   * shares (it reads the same root instance), and the worker precaches it like every other file.
   */
  private startOfflineShell(): void {
    import('./core/offline/offline-shell.service')
      .then(({ OfflineShellService }) => this.injector.get(OfflineShellService).init())
      .catch((e) => console.warn('[offline] could not load the service worker shell', e));
  }


  private findData(routeSnapshot: ActivatedRouteSnapshot): RouteConf | null {
    if (!routeSnapshot) return null;

    if (routeSnapshot.data && routeSnapshot.data['routeConf']) {
      return routeSnapshot.data['routeConf'];
    }



    for (const child of routeSnapshot.children) {
      return this.findData(child);
    }

    return null;

  }

  ngOnDestroy(): void {
    this.identitySwitchNotice?.unsubscribe();
    // Wipe the origin's localStorage on teardown, carrying over the few keys that must outlive it.
    // Guarded: a refused access here runs during destroy, where a throw has nowhere useful to go.
    //
    // The storage-failure ring is carried over for the same reason it is written at all -- it is
    // the ONLY copy of the evidence when the backend report never landed (offline, logged out),
    // and support pulls it with `copy(__quotaDiag())` long after the page that produced it. It
    // holds sizes and counts, never user data, so keeping it costs nothing on privacy.
    //
    // ALL THREE push keys describe the BROWSER PROFILE, not the session, and none holds user data.
    //
    // The posted-endpoint key is "the endpoint the backend has been told about". Wiping it would
    // make the status icon claim `inactive` on a device that is in fact working, forcing a
    // redundant re-POST on the next authenticated transition.
    //
    // The device id is worse to lose, and losing it is exactly the failure push-device-id.ts
    // exists to prevent ("one refusal would multiply into many"). The backend upserts the device
    // row on it, so a fresh id on the next boot ORPHANS the old row — it survives until the TTL
    // sweep or a 410 — and creates a second row for the same browser, so the admin's device list
    // grows a duplicate per teardown. The posted endpoint carried over above still matches the
    // live subscription, so the icon reads `active` for a row keyed to an id the backend has
    // never seen.
    //
    // The device SECRET goes with the id or not at all: it is what proves this browser owns the row
    // that id names, it is returned exactly once, and an id carried over without it leaves the next
    // registration unable to prove a possession the server still records — refused outright once
    // `galactus.push.require-device-secret` is switched on.
    //
    // The pending logout (`auth.logoutPending`, an offline logout the server has not heard yet) is
    // about the browser's COOKIE, which this wipe does not touch: lost, the next boot refreshes the
    // session the user logged out of instead of posting /v2/logout (review round 4, 26/10/2026).
    const preserved = [
      LOGOUT_PENDING_STORAGE_KEY,
      APP_LANG_STORAGE_KEY,
      QUOTA_DIAG_STORAGE_KEY,
      PUSH_POSTED_ENDPOINT_STORAGE_KEY,
      PUSH_DEVICE_ID_STORAGE_KEY,
      PUSH_DEVICE_SECRET_STORAGE_KEY,
    ]
      .map(key => [key, readStorage('local', key)] as const);
    clearStorage('local');
    for (const [key, value] of preserved) {
      if (value !== null) {
        writeStorage('local', key, value);
      }
    }
  }
}
